/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { useUIDSeed } from 'react-uid';
import {
    object, arrayOf, string, func, array, shape, bool,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Grid from '@material-ui/core/Grid';
import DeleteIcon from '@material-ui/icons/Delete';
import ReactHtmlParser from 'react-html-parser';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import AddToCart from '../../GraphqlFoodAddToCart/GraphqlFoodAddToCart';
import { addToCartDataType } from '../../common/types';

const styles = (theme) => ({
    product: {
        width: '100%',
        margin: '15px 15px 0px 0px',
    },
    productMobile: {
        width: '100%',
    },
    productHeading: {
        flex: '2 0 0',
        color: theme.palette.colorIconHeader,
        fontSize: '16px',
        lineHeight: '1.8em',
        fontWeight: '700',
        fontFamily: theme.palette.secondaryFontFamily,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        padding: '0 5px',
    },
    addedProductContainer: {
        border: `1px solid ${theme.palette.modalBorder}`,
        minHeight: '415px',
        height: 'auto',
        width: '100%',
        backgroundColor: theme.palette.modalBorder,
    },
    productBoxCollapsed: {
        maxHeight: 75,
        overflowY: 'hidden',
    },
    productBoxExpanded: {
        maxHeight: 500,
        overflowY: 'auto',
    },
    addedProductContainerMobile: {
        border: `1px solid ${theme.palette.modalBorder}`,
        width: '100%',
        backgroundColor: theme.palette.modalBorder,
        transition: 'max-height .5s ease',
    },
    showLessIcon: {
        textAlign: 'center',
        backgroundColor: theme.palette.cms?.addonsExpandBackground || '#d5d5da',
        borderTop: `1px solid ${theme.palette.cms?.addonsExpandBorder || '#b5a8a8'}`,
        outline: 'none',
    },
    addedProduct: {
        display: 'flex',
        flexDirection: 'row',
        height: '80px',
    },
    addedImage: {
        width: '100%',
        textAlign: 'left',
    },
    addedProductName: {
        fontSize: '16px',
        color: theme.palette.colorIconHeader,
        margin: '10px',
        fontWeight: '500',
        fontFamily: theme.typography.fontFamily,
        maxWidth: '145px',
        marginLeft: 'auto',
    },
    prices: {
        fontSize: '16px',
        fontWeight: '500',
        fontFamily: theme.typography.fontFamily,
        textAlign: 'Right',
        margin: '0',
        color: theme.palette.colorIconHeader,
        position: 'absolute',
        right: 5,
        bottom: 0,
    },
    productLine: {
        border: `1px solid ${theme.palette.productLine}`,
        width: 'calc(100% - 10px)',
        margin: '10px auto',
    },
    shippingContainer: {
        display: 'flex',
        flexDirection: 'row',
    },
    shipping: {
        fontSize: '12px',
        color: theme.palette.colorIconHeader,
        fontFamily: theme.typography.fontFamily,
        marginTop: '10px',
        paddingLeft: 30,
    },
    shippingMobile: {
        fontSize: '12px',
        color: theme.palette.colorIconHeader,
        fontFamily: theme.typography.fontFamily,
        marginTop: '10px',

    },
    total: {
        fontSize: '14px',
        color: theme.palette.colorIconHeader,
        fontWeight: '400',
        textAlign: 'right',
        fontFamily: theme.typography.secondaryFontFamily,
        margin: '10px 10px 0px 0px',
    },
    totalMobile: {
        fontSize: '13px',
        color: theme.palette.colorIconHeader,
        fontWeight: '400',
        textAlign: 'right',
        fontFamily: theme.typography.secondaryFontFamily,
        margin: '10px 10px 10px 0px',
    },
    AddtoCart: {
        margin: 0,
    },
    AddToCartMobile: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9,
        padding: 5,
        backgroundColor: theme.palette.white,
        borderTop: `1px solid ${theme.palette.productLine}`,
        '& > div': {
            margin: 0,
        },
    },
    addonContainer: {
        padding: 5,
    },
    selectionContainer: {
        padding: 5,
    },
    itemHeading: {
        fontSize: 16,
    },
    itemHeadingMobile: {
        fontSize: 12,
    },
    itemRow: {
        fontSize: 13,
    },
    itemRowMobile: {
        fontSize: 11,
    },
    relative: {
        position: 'relative',
    },
    delete: {
        fontSize: 18,
        cursor: 'pointer',
    },
    icons: {
        verticalAlign: 'middle',
    },
});

const SelectedAddonsContainer = ({
    classes,
    product,
    skuData,
    removeAddon,
    selectedAddons,
    product: {
        brandId,
        partNumber,
        productType,
    },
    categoryId,
    subscription,
    subscriptionDetails,
    presentation_family,
    disableAddToCart,
    isAddToCartEnabled,
    categoryPath,
    categoryName,
    handleMiniCartModalClick,
    closeAddonModal,
    isMobile,
    handleMobileAgeVerification,
    addToCartData,
    mobileAgeVerificationOpen,
    selectedSku,
    wineValidation,
}) => {
    const seed = useUIDSeed();
    const [showMore, setShowMore] = useState(false);
    /*
    * @description design the object of add on selection for cart functionality
    */
    const getAddons = () => {
        const addonsComponents = [];
        selectedAddons.forEach((addon) => {
            const addonItem = {
                brandCode: brandId,
                productCode: addon.partNumber,
                componentType: 'ADDON',
                productId: addon.legacyId,
                quantity: addon.qty,
            };
            if (addon.ageVerifyFlag) {
                addonItem.wineVerify = wineValidation?.wineVerify;
            }
            addonsComponents.push(addonItem);
        });
        return addonsComponents;
    };

    /*
    * @description design the object of add on selection for cart functionality
    */
    const hasWine = () => {
        const addons = (selectedAddons || []).filter((addon) => (addon.ageVerifyFlag));
        return (addons?.length > 0);
    };

    const buildImgPath = (path, name) => `${path + name}x.jpg`;

    /*
    * @description return the sale price of addon item
    * @param {array} prices
    */
    const getSalePrice = (prices) => {
        const salePrice = prices.filter((price) => price.type === 'sale');
        if (salePrice.length > 0) {
            return salePrice[0]?.value;
        }
        return 0;
    };

    /*
   * @description return total amount of product with addons
   * @param {float} itemPrice
   */
    const generateProductTotal = (itemPrice) => {
        const priceArray = (selectedAddons || []).map((addon) => getSalePrice(addon.prices) * addon.qty);
        priceArray.push(itemPrice || 0);
        return (priceArray || []).reduce((a, b) => a + b, 0);
    };

    /*
    * @description render shipping section and price of product and addons
    */
    const shippingSection = () => (
        <Grid container className={classes.shippingContainer}>
            <Grid item sm={7} xs={8}>
                {!isMobile && (
                    <ul className={classes.shipping}>
                        <li>Shipping will be calculated at checkout</li>
                    </ul>
                )}
                {isMobile && (
                    <div className={classes.shippingMobile}>
                        Shipping will be calculated at checkout
                    </div>
                )}

            </Grid>
            <Grid item sm={5} xs={4}>
                <p className={isMobile ? classes.totalMobile : classes.total}>
                    Total: <b>${generateProductTotal(skuData?.[0]?.prices?.[1]?.value).toFixed(2)}</b>
                </p>
            </Grid>
        </Grid>
    );

    return (
        <div className={isMobile ? classes.productMobile : classes.product}>
            {!isMobile && (<div className={classes.productHeading}>Your selections</div>)}
            <div className={isMobile ? `${classes.addedProductContainerMobile} ${(!showMore ? classes.productBoxCollapsed : classes.productBoxExpanded)}` :  classes.addedProductContainer}>
                <Grid container className={classes.selectionContainer}>
                    <Grid item sm={5} xs={3}>
                        <img src={buildImgPath(product?.image?.path, product?.image?.name)} alt={product?.image?.name} className={classes.addedImage} />
                    </Grid>
                    <Grid item sm={7} xs={9} className={classes.relative}>
                        <p className={classes.addedProductName}>{ReactHtmlParser(product.name)}</p>
                        <span className={classes.prices}>${skuData?.[0]?.prices?.[1]?.value}</span>
                    </Grid>
                </Grid>

                {selectedAddons?.length > 0
                    ? (
                        <>
                            <div className={classes.productLine} />
                            <Grid container className={classes.addonContainer}>
                                <Grid container className={isMobile ? classes.itemHeadingMobile : classes.itemHeading}>
                                    <Grid item sm={7} xs={7}> <b>Selected Add-ons:</b></Grid>
                                    <Grid item sm={2} xs={2}> <b>Qty</b></Grid>
                                    <Grid item sm={2} xs={2}> <b>Price</b></Grid>
                                </Grid>
                                {selectedAddons.map((item) => (
                                    <Grid key={seed(item)} container className={isMobile ? classes.itemRowMobile : classes.itemRow}>
                                        <Grid item sm={7} xs={7}>{ReactHtmlParser(item.name)}</Grid>
                                        <Grid item sm={2} xs={2}>{item.qty}</Grid>
                                        <Grid item sm={2} xs={2}>${getSalePrice(item.prices)}</Grid>
                                        <Grid item sm={1} xs={1}>
                                            <DeleteIcon className={classes.delete} onClick={() => removeAddon(item)} />
                                        </Grid>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )
                    : null}

                <div className={classes.productLine} />
                {shippingSection()}
            </div>
            {isMobile && (
                <div
                    onClick={() => setShowMore(!showMore)}
                    onKeyDown={() => { }}
                    tabIndex="0"
                    role="button"
                    className={classes.showLessIcon}
                >
                    {!showMore ? (
                        <span>Show More <ExpandMoreIcon className={classes.icons} /></span>
                    ) : (
                        <span>Show Less <ExpandLessIcon className={classes.icons} /></span>
                    )}
                </div>
            )}
            <div className={isMobile ? classes.AddToCartMobile : classes.AddtoCart}>
                <AddToCart
                    fullWidth
                    product={product}
                    brandId={brandId}
                    selectedItemId={selectedSku?.id}
                    productType={productType}
                    partNumber={partNumber}
                    categoryId={categoryId}
                    subscription={subscription}
                    subscriptionDetails={subscriptionDetails}
                    presentation_family={presentation_family}
                    disableAddToCart={disableAddToCart}
                    isAddToCartEnabled={isAddToCartEnabled}
                    categoryPath={categoryPath}
                    categoryName={categoryName}
                    hasWine={hasWine()}
                    addons={getAddons()}
                    closeAddonModal={closeAddonModal}
                    handleMiniCartModalClick={handleMiniCartModalClick}
                    mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                    handleMobileAgeVerification={handleMobileAgeVerification}
                    addToCartData={addToCartData}
                />

            </div>
        </div>
    );
};

SelectedAddonsContainer.propTypes = {
    classes: object.isRequired,
    product: shape({
        id: string.isRequired,
        name: string.isRequired,
        brandId: string.isRequired,
        partNumber: string.isRequired,
    }).isRequired,
    removeAddon: func,
    selectedAddons: arrayOf(shape({
        name: string,
        partNumber: string,
        id: string,
    })),
    skuData: arrayOf(shape({
        prices: arrayOf({
            value: string.isRequired,
        }),
    })).isRequired,
    wineValidation: shape({
        wineAvailability: object, // object type array
        wineVerify: shape({
            birthDay: 'string',
            birthMonth: 'string',
            birthYear: 'string',
            minAge: 'string',
        }),
    }),
    categoryId: string,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
    }),
    presentation_family: string.isRequired,
    disableAddToCart: bool,
    isAddToCartEnabled: bool,
    categoryPath: string,
    categoryName: string,
    closeAddonModal: func,
    handleMiniCartModalClick: func.isRequired,
    isMobile: bool,
    handleMobileAgeVerification: func,
    mobileAgeVerificationOpen: bool,
    addToCartData: addToCartDataType,
    selectedSku: shape({
        id: string,
    }),
};

SelectedAddonsContainer.defaultProps = {
    removeAddon: () => {},
    closeAddonModal: () => {},
    handleMobileAgeVerification: () => {},
    selectedAddons: [],
    isAddToCartEnabled: true,
    subscriptionDetails: {},
    disableAddToCart: false,
    subscription: {},
    categoryId: '',
    categoryPath: '',
    categoryName: '',
    isMobile: false,
    mobileAgeVerificationOpen: false,
    addToCartData: {},
    wineValidation: {},
    selectedSku: {},
};

export default (withStyles(styles)(SelectedAddonsContainer));
